export const VENAQQI_VAPID_PUBLIC_KEY = "BFhXBT5SVYKCqyhGWbBr5Mq_Dr-aEz1886cqDcf1U5fKF0Ur1ppjONlCAuYQOuKd-EH0C4JmDSuJCq4BwSgFiGk";

const browserScope = () => typeof window !== "undefined" ? window : {};

export const urlBase64ToUint8Array = value => {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map(char => char.charCodeAt(0)));
};

export const pushCapability = (scope = browserScope()) => ({
  serviceWorker: !!scope?.navigator?.serviceWorker,
  notifications: !!scope?.Notification,
  pushManager: !!scope?.PushManager,
  supported: !!scope?.navigator?.serviceWorker && !!scope?.Notification && !!scope?.PushManager,
});

export const serializeSubscription = subscription => {
  if (!subscription) return null;
  const json = subscription.toJSON ? subscription.toJSON() : subscription;
  return { endpoint: json.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth };
};

const bindSubscription = async ({ supabase, userId, subscription, scope = browserScope() }) => {
  if (!userId || !subscription) return false;
  const payload = serializeSubscription(subscription);
  if (!payload?.endpoint || !payload.p256dh || !payload.auth) throw new Error("Suscripción push incompleta");
  const { data, error } = await supabase.rpc("bind_current_push_subscription", {
    p_endpoint: payload.endpoint,
    p_p256dh: payload.p256dh,
    p_auth: payload.auth,
    p_user_agent: scope?.navigator?.userAgent || null,
  });
  if (error) throw error;
  return data === true;
};

export const flushPendingVenaqqiPush = async ({ supabase, userId }) => {
  if (!userId) return 0;
  const { data, error } = await supabase.rpc("flush_current_user_push");
  if (error) throw error;
  return Number(data || 0);
};

export const subscribeToVenaqqiPush = async ({ supabase, userId, scope = browserScope() }) => {
  if (!userId) throw new Error("Inicia sesión para activar notificaciones");
  if (!pushCapability(scope).supported) throw new Error("Este navegador no admite notificaciones push");
  const permission = await scope.Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permiso de notificaciones no concedido");
  const registration = await scope.navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VENAQQI_VAPID_PUBLIC_KEY) });
  await bindSubscription({ supabase, userId, subscription, scope });
  await flushPendingVenaqqiPush({ supabase, userId });
  return subscription;
};

export const unsubscribeFromVenaqqiPush = async ({ supabase, userId, scope = browserScope() }) => {
  if (!pushCapability(scope).serviceWorker) return;
  const registration = await scope.navigator.serviceWorker.ready;
  const subscription = await registration.pushManager?.getSubscription?.();
  const endpoint = subscription?.endpoint || null;
  if (subscription) await subscription.unsubscribe();
  if (userId) {
    const { error } = await supabase.rpc("unbind_current_push_subscription", { p_endpoint: endpoint });
    if (error) throw error;
  }
};

export const readPushState = async ({ supabase, userId, scope = browserScope() }) => {
  const capability = pushCapability(scope);
  if (!capability.supported || !userId) return { ...capability, permission: scope?.Notification?.permission || "unsupported", subscribed: false, preferences: null };
  const registration = await scope.navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  // A browser subscription can survive logout/relogin. Re-bind it to the current
  // authenticated Venaqqi account so the UI and server never drift apart.
  if (subscription && scope.Notification.permission === "granted") {
    try {
      await bindSubscription({ supabase, userId, subscription, scope });
      await flushPendingVenaqqiPush({ supabase, userId });
    } catch { /* state below remains readable even if repair/replay fails */ }
  }

  const { data } = await supabase.from("notification_preferences").select("moderation,novedades,nuevos_lugares,web_push").eq("user_id", userId).maybeSingle();
  return { ...capability, permission: scope.Notification.permission, subscribed: !!subscription, preferences: data || { moderation: true, novedades: true, nuevos_lugares: true, web_push: true } };
};

export const updateNotificationPreferences = async ({ supabase, userId, preferences }) => {
  const payload = { user_id: userId, ...preferences, updated_at: new Date().toISOString() };
  const { data, error } = await supabase.from("notification_preferences").upsert(payload, { onConflict: "user_id" }).select("moderation,novedades,nuevos_lugares,web_push").single();
  if (error) throw error;
  if (data?.web_push !== false) {
    try { await flushPendingVenaqqiPush({ supabase, userId }); } catch { /* next refresh retries pending rows */ }
  }
  return data;
};
